import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Globe, Mail } from 'lucide-react';
import { applySeo } from '../../utils/seo';
import { ogBrandImageUrl } from '../../constants/ogBrand';
import { normalizeUiLang } from '../../utils/localePreference';
import { removeBootSplash } from '../../utils/bootSplash';
import { digitalCardStyles } from '../digitalcard/digitalCardStyles';
import { fieldTeamStyles } from './fieldTeamStyles';
import FieldTeamNav from './FieldTeamNav';
import FieldTeamFooter from './FieldTeamFooter';
import CrmSignupModal from '../crm/CrmSignupModal';
import { FIELD_TEAM_SEO_KEYWORDS } from './fieldTeamSeo';
import { CONTACT_PATH, fieldTeamLandingUrl } from './fieldTeamRoutes';

const FieldTeamContactPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resolvedLang = normalizeUiLang(i18n.resolvedLanguage || i18n.language);
  const demoIntent = String(searchParams.get('intent') || '').toLowerCase() === 'demo';
  const [signupOpen, setSignupOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: demoIntent ? t('ftl_contact_demo_subject') : '',
    message: demoIntent ? t('ftl_contact_demo_message') : '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const openSignup = useCallback(() => setSignupOpen(true), []);

  useEffect(() => {
    removeBootSplash();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.tcards.org';
    const title = t('ftl_contact_seo_title');
    const description = t('ftl_contact_seo_desc');
    const canonical = `${origin}${CONTACT_PATH}`;
    const image = ogBrandImageUrl(origin, 'crm');
    const dispose = applySeo({
      title,
      description,
      keywords: FIELD_TEAM_SEO_KEYWORDS,
      image,
      imageAlt: t('ftl_seo_site_name'),
      url: canonical,
      canonicalUrl: canonical,
      siteName: t('ftl_seo_site_name'),
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: title,
        url: canonical,
        description,
      },
    });
    return dispose;
  }, [t, resolvedLang]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.message) {
      setMsg({ type: 'err', text: t('ftl_contact_fill_required') });
      return;
    }
    setSubmitting(true);
    setMsg(null);
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cardOwnerId: 'homepage',
          cardOwnerEmail: 'info@tcards.org',
          cardOwnerName: 'TCards Field Team CRM',
          fullName: form.name,
          email: form.email,
          phoneNumber: form.phone || undefined,
          message: `[${form.subject || t('ftl_contact_subject')}]\n${form.message}`,
          source: 'field_team_contact',
        }),
      });
      if (!res.ok && res.status !== 202) throw new Error('Failed');
      setMsg({ type: 'ok', text: t('ftl_contact_success') });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      setMsg({ type: 'err', text: t('ftl_contact_error') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dcl-root dcl-root--linktree ftl-root">
      <style>{digitalCardStyles}</style>
      <style>{fieldTeamStyles}</style>
      <FieldTeamNav activePath={CONTACT_PATH} onGetStarted={openSignup} />

      <section className="dcl-section dcl-section--mesh">
        <div className="dcl-container">
          <div className="dcl-contact-grid">
            <div>
              <div className="dcl-section-badge">
                <Mail size={14} />
                {t('ftl_nav_contact')}
              </div>
              <h1 className="dcl-section-title">{t('ftl_contact_title')}</h1>
              <p className="dcl-section-sub">
                {demoIntent ? t('ftl_contact_demo_sub') : t('ftl_contact_sub')}
              </p>
              <p className="ftl-contact-back">
                <Link to={fieldTeamLandingUrl()} className="dcl-btn dcl-btn--ghost dcl-btn--sm dcl-btn--pill">
                  <ArrowLeft size={16} />
                  {t('ftl_contact_back')}
                </Link>
              </p>
              <div className="dcl-contact-info">
                <div className="dcl-contact-info-item">
                  <span className="dcl-contact-info-icon dcl-contact-info-icon--mail">
                    <Mail size={16} />
                  </span>
                  <a href="mailto:info@tcards.org" style={{ color: '#2563EB', fontWeight: 600, textDecoration: 'none' }}>
                    info@tcards.org
                  </a>
                </div>
                <div className="dcl-contact-info-item">
                  <span className="dcl-contact-info-icon dcl-contact-info-icon--web">
                    <Globe size={16} />
                  </span>
                  <span>www.tcards.org</span>
                </div>
              </div>
            </div>

            <div className="dcl-contact-card">
              <form className="dcl-form" onSubmit={handleSubmit}>
                <input
                  className="dcl-input"
                  placeholder={t('ftl_contact_name')}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
                <input
                  className="dcl-input"
                  type="email"
                  placeholder={t('ftl_contact_email')}
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                />
                <input
                  className="dcl-input"
                  placeholder={t('ftl_contact_phone')}
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
                <input
                  className="dcl-input"
                  placeholder={t('ftl_contact_subject')}
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                />
                <textarea
                  className="dcl-textarea"
                  placeholder={t('ftl_contact_message')}
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  required
                />
                <button type="submit" className="dcl-btn dcl-btn--primary" disabled={submitting}>
                  {submitting ? t('ftl_contact_sending') : t('ftl_contact_send')}
                </button>
                {msg && (
                  <div className={`dcl-form-msg dcl-form-msg--${msg.type}`}>{msg.text}</div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <FieldTeamFooter onGetStarted={openSignup} />
      <CrmSignupModal
        open={signupOpen}
        onClose={() => setSignupOpen(false)}
        onComplete={() => {
          setSignupOpen(false);
          navigate('/crm', { replace: true });
        }}
      />
    </div>
  );
};

export default FieldTeamContactPage;
